import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatPrice } from '../utils/helpers';
import { useCartStore } from '../store';
import { ShoppingBag, Eye, Heart } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ProductQuickView from './ProductQuickView';

const ProductCard = ({ product, index = 0 }) => {
  const [showQuickView, setShowQuickView] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const addItem = useCartStore((state) => state.addItem);
  
  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowQuickView(true);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted(!isWishlisted);
    toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist');
  };
  
  const imageUrl = product.image_urls?.[0] || 'https://images.unsplash.com/photo-1736436789706-005f2218a96d?w=400';

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        className="card-product group"
        data-testid={`product-card-${product.id}`}
      >
        <Link to={`/product/${product.id}`}>
          {/* Image */}
          <div className="img-zoom aspect-[3/4] relative">
            <img
              src={imageUrl}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            
            {/* Wishlist Button */}
            <button
              onClick={handleWishlist}
              className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-all duration-300 ${
                isWishlisted 
                  ? 'bg-red-500 text-white' 
                  : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500'
              }`}
            >
              <Heart size={18} weight={isWishlisted ? 'fill' : 'regular'} />
            </button>
            
            {/* Quick Actions Overlay */}
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center gap-3">
              <button
                onClick={handleAddToCart}
                data-testid={`add-to-cart-${product.id}`}
                className="bg-white px-4 py-2 rounded-full shadow-lg hover:bg-primary hover:text-white transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 flex items-center gap-2"
              >
                <ShoppingBag size={16} />
                <span className="font-body text-sm">Add</span>
              </button>
              <button
                onClick={handleQuickView}
                data-testid={`quick-view-${product.id}`}
                className="bg-white p-2 rounded-full shadow-lg hover:bg-primary hover:text-white transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 delay-75"
              >
                <Eye size={18} />
              </button>
            </div>
            
            {/* Category Badge */}
            {product.category_name && (
              <span className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1 text-xs font-body uppercase tracking-wider text-secondary shadow-sm">
                {product.category_name}
              </span>
            )}
            
            {/* Out of Stock Badge */}
            {product.quantity === 0 && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="bg-white text-red-500 px-4 py-2 font-body text-sm uppercase tracking-wider">
                  Sold Out
                </span>
              </div>
            )}

            {/* Low Stock Badge */}
            {product.quantity > 0 && product.quantity < 5 && (
              <span className="absolute bottom-3 left-3 bg-orange-500 text-white px-2 py-1 text-xs font-body">
                Only {product.quantity} left
              </span>
            )}
          </div>
          
          {/* Info */}
          <div className="p-4 text-center">
            <h3 className="font-display text-lg text-secondary group-hover:text-primary transition-colors duration-300 mb-1 line-clamp-1">
              {product.name}
            </h3>
            {(product.type || product.material) && (
              <p className="font-body text-xs text-text-muted mb-1 line-clamp-1">
                {product.type}{product.type && product.material && ' • '}{product.material}
              </p>
            )}
            <p className="font-body text-primary font-medium text-lg">
              {formatPrice(product.selling_rate)}
              {product.mrp && product.mrp > product.selling_rate && (
                <span className="text-text-muted line-through ml-2 text-sm font-normal">
                  {formatPrice(product.mrp)}
                </span>
              )}
            </p>
            <p className="font-body text-green-600 text-sm">
              Free Shipping
            </p>
          </div>
        </Link>
      </motion.div>

      {/* Quick View Modal */}
      <ProductQuickView
        product={product}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
      />
    </>
  );
};

export default ProductCard;
